'use client'

import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../../_components/_ui/background-beams'
import Link from 'next/link'

export default function SharingPage() {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10 container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="mb-8">
                        <Link href="/support/documentation" className="text-indigo-400 hover:text-indigo-300 text-sm">
                            ← Back to Documentation
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-white mb-6">
                        Sharing & Security Features
                    </h1>
                    
                    <div className="prose prose-lg prose-invert max-w-none">
                        <p className="text-gray-300 text-lg mb-8">
                            Autofocus provides powerful sharing capabilities with granular security controls. 
                            From QR codes to password protection, creators have complete control over who can 
                            access their content and how it's shared.
                        </p>

                        {/* QR Code System */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                📱 QR Code Generation & Sharing
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-3">Dynamic QR Code Generation</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Autofocus automatically generates QR codes for any page or block, making it easy to share 
                                        content in physical spaces or mobile contexts.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Features</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• High error correction (Level H) for logo overlay</li>
                                                <li>• Custom brand colors and styling</li>
                                                <li>• Automatic URL encoding</li>
                                                <li>• Copy-to-clipboard functionality</li>
                                                <li>• Download as PNG for printing</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Technical Implementation</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// QR Code Generation
import QRCode from 'qrcode'

const options = {
  errorCorrectionLevel: 'H',
  margin: 1,
  width: 300,
  color: {
    dark: '#0d4a94',
    light: '#ffffff'
  }
}

await QRCode.toCanvas(canvas, url, options)`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">Usage Scenarios</h3>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-blue-400 mb-2">Business Cards</h4>
                                            <p className="text-gray-300 text-sm">Link to your complete profile instead of cramming information on paper</p>
                                        </div>
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-green-400 mb-2">Event Sharing</h4>
                                            <p className="text-gray-300 text-sm">Quick access to portfolios, menus, or event information</p>
                                        </div>
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-orange-400 mb-2">Print Materials</h4>
                                            <p className="text-gray-300 text-sm">Bridge physical and digital marketing with QR codes</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Social Sharing */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                🌐 Social Media Integration
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-3">Platform Support</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Built-in sharing for major social platforms with custom messages and optimal formatting.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Supported Platforms</h4>
                                            <div className="space-y-2 text-sm">
                                                <div className="bg-neutral-800 p-2 rounded flex justify-between">
                                                    <span className="text-blue-400">Twitter/X</span>
                                                    <span className="text-gray-400">Text + URL</span>
                                                </div>
                                                <div className="bg-neutral-800 p-2 rounded flex justify-between">
                                                    <span className="text-blue-600">Facebook</span>
                                                    <span className="text-gray-400">URL sharing</span>
                                                </div>
                                                <div className="bg-neutral-800 p-2 rounded flex justify-between">
                                                    <span className="text-blue-700">LinkedIn</span>
                                                    <span className="text-gray-400">Professional sharing</span>
                                                </div>
                                                <div className="bg-neutral-800 p-2 rounded flex justify-between">
                                                    <span className="text-green-500">WhatsApp</span>
                                                    <span className="text-gray-400">Direct messaging</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Share URLs</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Platform-specific URL generation
const SHARE_PLATFORMS = {
  twitter: (url, title) => 
    \`https://twitter.com/intent/tweet?url=\${url}&text=\${title}\`,
  facebook: (url) => 
    \`https://www.facebook.com/sharer/sharer.php?u=\${url}\`,
  linkedin: (url, title) => 
    \`https://www.linkedin.com/sharing/share-offsite/?url=\${url}&title=\${title}\`,
  whatsapp: (url, title) => 
    \`https://wa.me/?text=\${title} \${url}\`
}`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Link Optimization</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Automatic Copy to Clipboard</h4>
                                            <p className="text-gray-300 text-sm mb-2">
                                                When share popup opens, URLs are automatically copied for quick pasting
                                            </p>
                                            <div className="bg-neutral-800 p-2 rounded text-xs text-gray-400">
                                                navigator.clipboard.writeText(url)
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Clean URLs</h4>
                                            <p className="text-gray-300 text-sm mb-2">
                                                Autofocus generates clean, readable URLs perfect for sharing
                                            </p>
                                            <div className="bg-neutral-800 p-2 rounded text-xs text-gray-400">
                                                autofoc.us/nate/portfolio
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Password Protection */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                🔐 Password Protection System
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-3">Page-Level Security</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Protect sensitive content with password-protected pages and redirect links. 
                                        Perfect for client work, private collections, or exclusive content.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Features</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Per-page password protection</li>
                                                <li>• Custom password requirements</li>
                                                <li>• Secure server-side verification</li>
                                                <li>• No password exposure in client code</li>
                                                <li>• Session-based access control</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Security Implementation</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Passwords stored in encrypted metadata</li>
                                                <li>• Server-side verification only</li>
                                                <li>• No password hints or recovery</li>
                                                <li>• Rate limiting on password attempts</li>
                                                <li>• Automatic session timeout</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Redirect Pages with Protection</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Create protected redirect pages that require password verification before forwarding users to external URLs.
                                    </p>
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-yellow-400 mb-2">API Verification Process</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`// Password verification API
POST /api/verify-redirect-password
{
  "username": "creator",
  "pageSlug": "private-link", 
  "password": "userInput"
}

// Response (never exposes actual password)
{
  "success": true | false
}

// On success: redirect to target URL
// On failure: show error message`}
                                        </pre>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">Use Cases</h3>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-cyan-400 mb-2">Client Galleries</h4>
                                            <p className="text-gray-300 text-sm">Share photography or design work with specific clients</p>
                                        </div>
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-pink-400 mb-2">Premium Content</h4>
                                            <p className="text-gray-300 text-sm">Exclusive content for subscribers or members</p>
                                        </div>
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-emerald-400 mb-2">Private Links</h4>
                                            <p className="text-gray-300 text-sm">Secure access to sensitive documents or resources</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content Visibility Controls */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                👁️ Content Visibility & Access Control
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Public vs Private Content</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Public Pages</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Accessible without authentication</li>
                                                <li>• Indexed by search engines</li>
                                                <li>• Social media preview cards</li>
                                                <li>• QR code and link sharing</li>
                                                <li>• API access available</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-red-400 mb-2">Private Pages</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Authentication required</li>
                                                <li>• Hidden from search engines</li>
                                                <li>• No social media previews</li>
                                                <li>• Access logging and analytics</li>
                                                <li>• Role-based permissions</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-yellow-400 mb-3">Link Shortening & Tracking</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Autofocus provides clean, short URLs that are easy to share and remember, 
                                        with built-in analytics for tracking engagement.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">URL Structure</h4>
                                            <div className="space-y-2 text-sm">
                                                <div className="bg-neutral-800 p-2 rounded">
                                                    <span className="text-blue-400">Profile:</span>
                                                    <span className="text-gray-300 ml-2">autofoc.us/username</span>
                                                </div>
                                                <div className="bg-neutral-800 p-2 rounded">
                                                    <span className="text-green-400">Page:</span>
                                                    <span className="text-gray-300 ml-2">autofoc.us/username/page</span>
                                                </div>
                                                <div className="bg-neutral-800 p-2 rounded">
                                                    <span className="text-purple-400">Block:</span>
                                                    <span className="text-gray-300 ml-2">autofoc.us/username/page#block-id</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Analytics Tracking</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Page view analytics</li>
                                                <li>• Referrer tracking</li>
                                                <li>• Geographic data</li>
                                                <li>• Device and browser info</li>
                                                <li>• Time-based analytics</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* SEO & Meta Tags */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                🔍 SEO & Social Media Optimization
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Automatic Meta Tag Generation</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Autofocus automatically generates optimal meta tags for social media sharing 
                                        and search engine optimization.
                                    </p>
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-blue-400 mb-2">Generated Meta Tags</h4>
                                        <pre className="text-xs text-gray-300 overflow-x-auto">
{`<!-- Open Graph (Facebook, LinkedIn) -->
<meta property="og:title" content="Page Title - Creator Name" />
<meta property="og:description" content="Page description..." />
<meta property="og:image" content="https://autofoc.us/preview.jpg" />
<meta property="og:url" content="https://autofoc.us/username/page" />

<!-- Twitter Cards -->
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="Page Title - Creator Name" />
<meta name="twitter:description" content="Page description..." />
<meta name="twitter:image" content="https://autofoc.us/preview.jpg" />

<!-- SEO -->
<meta name="description" content="Page description..." />
<link rel="canonical" href="https://autofoc.us/username/page" />`}
                                        </pre>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Custom Preview Cards</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Dynamic Previews</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Profile picture as default image</li>
                                                <li>• First image block as preview</li>
                                                <li>• Custom page descriptions</li>
                                                <li>• Brand-consistent styling</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-200 mb-2">Platform Optimization</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Twitter card format</li>
                                                <li>• Facebook Open Graph</li>
                                                <li>• LinkedIn optimization</li>
                                                <li>• WhatsApp link previews</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">
                                🚀 Explore More
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Link href="/support/documentation/auth" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-red-400 mb-1">Authentication & Roles →</div>
                                    <div className="text-gray-300 text-sm">Deep dive into user permissions and security</div>
                                </Link>
                                <Link href="/support/documentation/advanced" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-yellow-400 mb-1">Advanced Features →</div>
                                    <div className="text-gray-300 text-sm">Analytics, admin tools, and platform extensibility</div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}